/**
 * A bill — or a prepared ticket, which prints through the same preparation. Port of web
 * `printHelper.js` `printSale` (:46-217), the canonical copy.
 *
 * Plans only: it decides the library input, the destination and the copy count, and whether the
 * cash drawer opens first. Rendering (text or HTML) and sending happen later, so this stays a pure
 * function of the sale and the context.
 */
import type { BillInput, DocumentKind, PaymentMethod, Piece, Plan, PrintContext } from "../types";
import { clone, isEmpty, toCount } from "../util";
import { drawerPiece } from "./drawer";

/**
 * v1 `checkBillReprint`: printed before, and the last print was not the automatic one. Mobile keyed
 * this on `printCount` instead; web is the reference.
 */
export function isBillReprint(sale: any): boolean {
  const log = sale?.printLog;
  return !!log && log.length > 1 && log[log.length - 1].auto !== true;
}

/**
 * Whether paying this sale opens the drawer: some payment mode on it has
 * `disableCashDrawerToggleField === 0`. No payment modes → no drawer.
 */
export function opensCashDrawer(paymentModes: any[] | undefined, methods: PaymentMethod[]): boolean {
  if (!paymentModes?.length) return false;
  const byMode: Record<string, PaymentMethod> = {};
  for (const method of methods) byMode[method.mode as string] = method; // last wins, as v1's arrayToMap
  return paymentModes.some((mode) => {
    const method = byMode[mode] || {};
    return Object.prototype.hasOwnProperty.call(method, "disableCashDrawerToggleField") && method.disableCashDrawerToggleField === 0;
  });
}

/**
 * v1 prepares a sale for the library in this order, and the order is observable (the reprint
 * suffix lands on whatever `billTitle` is at that point).
 */
export function planBill(input: BillInput, ctx: PrintContext, kind: DocumentKind = "bill"): Plan {
  const sale = input.sale;
  const printer = input.printer ?? ctx.printers.defaultPrinter ?? {};
  const isTicket = !!sale?.ticketNumber;
  const relay = ctx.device.isSlave && ctx.device.printBillFromMaster && !isTicket;

  // v1 shows "A default printer must be selected." and then sends to the empty printer anyway,
  // where it fails. The send is dropped here; what the user sees is the same.
  if (isEmpty(printer) && !relay && !ctx.device.builtInPrinter) return { pieces: [], skipped: [{ reason: "noDefaultPrinter" }] };
  // v1 throws inside its own try/catch on a sale without an item list, and prints nothing.
  if (!Array.isArray(sale?.saleItems)) return { pieces: [], skipped: [{ reason: "invalidSale" }] };

  const billTemplate = ctx.business.billTemplate;
  const ticketSettings = ctx.posSettings?.tickets;
  const data = clone(sale);

  // a punched name, when the item has one, is what the kitchen and the customer read
  data.saleItems = data.saleItems.map((item: any) => ({ ...item, shortName: item.punchedName || item.shortName }));

  // v1's Android-wrapper branch: a host that cannot print pictures gets none of them. Blanked
  // rather than deleted, exactly as v1 does — the template reads these keys, not their absence.
  if (ctx.device.canPrintImages === false) {
    data.printQRCode = false;
    data.footerOnOpen = false;
    data.billFooter = "";
    data.signatureImageId = "";
    if (data.billHeader) data.billHeader.imageDataId = "";
  }

  let copies: unknown = input.copies === undefined ? 1 : input.copies;
  if (input.copiesFromChannel) {
    // v1 `printSale` overrides the passed count with the SALE's channel — including when that
    // channel carries no `billPrintCopies` at all, which is why this assignment is unconditional
    // (v1 then prints nothing: `while (undefined)` never runs).
    const saleChannel = data.channelDefId && ctx.channels[data.channelDefId];
    if (saleChannel) copies = saleChannel.billPrintCopies;
    // When the sale's channel is not in `channelDef`, v1 keeps the count it was PASSED, which
    // `handlePrintOnClose` computed from the OPERATOR's channel (`getSelectedChannel()`) — and
    // which reaches `printSale` through a `copies = 1` default parameter, so an operator channel
    // with no `billPrintCopies` prints ONE copy rather than none. `??` is that default.
    else copies = ctx.channels[ctx.selectedChannelDefId as string]?.billPrintCopies ?? copies;
  }

  if (data.mid && !billTemplate?.skipRistaQuote) data.showRistaQuote = true;

  const params: any = {
    maxChars: printer?.printWidth ?? 46,
    billTemplate: billTemplate ? clone(billTemplate) : "",
    posPreferences: ctx.posSettings,
  };
  if (input.giftReceipt) params.templateOptions = { compact: true, title: "Gift Receipt" };
  if (!data.mid) {
    // no sale id → a ticket: compact layout, the ticket settings' font and PIN
    params.templateOptions = params.templateOptions || {};
    params.templateOptions.fontSize = ticketSettings?.fontSize ? ticketSettings.fontSize : "regular";
    params.templateOptions.compact = true;
    params.templateOptions.pinSupport = ticketSettings?.pinSupport;
  }
  params.businessType = ctx.business.businessType;

  // empty blocks would still draw their headings
  if (isEmpty(data.customerInfo)) {
    delete data.customerInfo;
    if (isEmpty(data.deliveryInfo?.customerInfo)) delete data.deliveryInfo?.customerInfo;
    if (data.deliveryInfo && isEmpty(data.deliveryInfo.contactInfo)) delete data.deliveryInfo.contactInfo;
  }
  if (isEmpty(data.deliveryInfo)) delete data.deliveryInfo;
  if (isEmpty(data.serviceLabel)) delete data.serviceLabel;

  const reprint = isBillReprint(data);
  if (reprint) data.billTitle = data.billTitle + "(Reprint)";
  if (billTemplate?.showPhoneNumber) data.showPhoneNumber = true;

  const pieces: Piece[] = [];
  // The drawer opens on the LOCAL printer even when the bill relays — v1 does the same, and a
  // slave with no printer of its own has nothing to kick, so that send is dropped. v1's
  // Android-wrapper branch (the host that cannot print images) never opens it.
  const kicksDrawer = !isTicket && !reprint && !input.manual && ctx.device.canPrintImages !== false;
  if (kicksDrawer && !isEmpty(printer) && opensCashDrawer(sale.paymentModes, ctx.paymentMethods)) {
    pieces.push(drawerPiece(printer));
  }
  pieces.push({
    document: kind,
    // v1 order: a slave's bill relays, then a Pine Labs terminal prints on its own hardware
    destination: relay ? { to: "relay" } : ctx.device.builtInPrinter ? { to: "builtIn" } : { to: "printer", printer },
    copies: toCount(copies),
    source: { kind: "template", data, params },
  });
  return { pieces, skipped: [] };
}
